import type { Dispatch, RefObject, SetStateAction, SyntheticEvent } from 'react'

import { useCallback, useMemo } from 'react'
import { debounce } from 'utils/helpers'
import { ScrollAligning, ScrollBehavior } from '../RowContainer.h'

interface IRowScrollHandlersProps {
  ref: RefObject<HTMLDivElement | null>
  currentIndex: number
  setCurrentIndex: Dispatch<SetStateAction<number | undefined>>
  visibleCards: number
  groupCells: number | undefined
  itemSize: number
  childrenCount: number
  extraCount: number
  gapBetweenCards: number
  rowOffset: number
  setRowScrollPosition: Dispatch<SetStateAction<number>>
}

export const useRowScrollHandlers = ({
  ref,
  extraCount = 0,
  childrenCount,
  currentIndex,
  visibleCards,
  groupCells,
  setRowScrollPosition,
}: IRowScrollHandlersProps) => {
  const elementToScroll = useMemo(() => {
    return groupCells || visibleCards
  }, [groupCells, visibleCards])
  const DELAY_SET_SCROLL_POSITION = 150

  const scrollToItem = ({
    nextItemIndex,
    behavior = ScrollBehavior.smooth,
    block = ScrollAligning.nearest,
    inline = ScrollAligning.end,
  }: {
    nextItemIndex: number
    behavior?: ScrollBehavior
    inline?: ScrollAligning
    block?: ScrollAligning
  }): void => {
    // add a bit delay to scroll to the item,
    // ensuring that the item is rendered before scrolling
    setTimeout(() => {
      ref.current?.children[nextItemIndex]?.scrollIntoView({
        behavior,
        block,
        inline,
      })
    }, 10)
  }

  const handleScroll = useCallback(
    debounce((_e: SyntheticEvent) => {
      if (ref.current) {
        setRowScrollPosition((_prev) => ref.current!.scrollLeft)
      }
    }, DELAY_SET_SCROLL_POSITION),
    []
  )

  const next = (): void => {
    const nextIndex =
      currentIndex + elementToScroll < childrenCount
        ? currentIndex + elementToScroll
        : childrenCount - 1
    scrollToItem({ nextItemIndex: nextIndex })
  }

  const prev = (): void => {
    const nextIndex =
      currentIndex - elementToScroll > visibleCards - (extraCount + 1 || 1)
        ? currentIndex - elementToScroll
        : visibleCards - (extraCount || 1)

    scrollToItem({ nextItemIndex: nextIndex })
  }

  return {
    next,
    prev,
    handleScroll,
    scrollToItem,
  }
}
